import React, { useState } from 'react';
import { X, Calendar, Pencil } from 'lucide-react';
import { Event } from '../types/supabase';
import SignupOptionsField from './SignupOptionsField';
import RecurrenceField, { EMPTY_RECURRENCE, RecurrenceValue } from './RecurrenceField';

const CATEGORY_OPTIONS = [
  { value: 'fellowship', label: 'Fellowship' },
  { value: 'service', label: 'Service' },
  { value: 'study', label: 'Study' },
  { value: 'prayer', label: 'Prayer' },
  { value: 'worship', label: 'Worship' },
  { value: 'outreach', label: 'Outreach' },
];

const EMPTY_FORM = {
  title: '',
  date: '',
  time: '',
  location: '',
  description: '',
  category: 'fellowship',
  timezone: 'America/Chicago',
  allow_volunteers: true,
  allow_attendees: false,
};

const formFromEvent = (event: Event) => ({
  title: event.title,
  date: event.date,
  time: event.time,
  location: event.location,
  description: event.description,
  category: event.category,
  timezone: event.timezone,
  allow_volunteers: event.allow_volunteers,
  allow_attendees: event.allow_attendees,
});

const recurrenceFromEvent = (event: Event): RecurrenceValue => ({
  is_recurring: event.is_recurring,
  recurrence_frequency: event.recurrence_frequency,
  recurrence_end_date: event.recurrence_end_date,
});

interface AdminAddEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (eventData: RecurrenceValue & {
    title: string;
    date: string;
    time: string;
    location: string;
    description: string;
    category: string;
    timezone: string;
    allow_volunteers: boolean;
    allow_attendees: boolean;
  }) => Promise<void>;
  event?: Event | null;
}

const AdminAddEventModal: React.FC<AdminAddEventModalProps> = ({ isOpen, onClose, onSubmit, event }) => {
  const isEditing = Boolean(event);
  const [formData, setFormData] = useState(() => (event ? formFromEvent(event) : EMPTY_FORM));
  const [recurrence, setRecurrence] = useState<RecurrenceValue>(() =>
    event ? recurrenceFromEvent(event) : EMPTY_RECURRENCE
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const categoryOptions = CATEGORY_OPTIONS.some(o => o.value === formData.category)
    ? CATEGORY_OPTIONS
    : [{ value: formData.category, label: formData.category }, ...CATEGORY_OPTIONS];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        ...recurrence,
        recurrence_end_date: recurrence.recurrence_end_date || null,
      });
      if (!isEditing) {
        setFormData(EMPTY_FORM);
        setRecurrence(EMPTY_RECURRENCE);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center space-x-3">
            {isEditing ? <Pencil className="w-6 h-6 text-blue-600" /> : <Calendar className="w-6 h-6 text-blue-600" />}
            <h2 className="text-2xl font-bold text-gray-900">{isEditing ? 'Edit Event' : 'Add New Event'}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Event Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Monday Night Bible Study"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                {isEditing && recurrence.is_recurring ? 'First Date *' : 'Date *'}
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                min={isEditing ? undefined : new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                Time *
              </label>
              <input
                type="time"
                id="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <RecurrenceField
            startDate={formData.date}
            time={formData.time}
            value={recurrence}
            onChange={setRecurrence}
          />

          <div>
            <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-1">
              Time Zone *
            </label>
            <select
              id="timezone"
              name="timezone"
              value={formData.timezone}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="America/Anchorage">Alaska Time (AKT)</option>
              <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
            </select>
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {categoryOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
              Location *
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., 123 Main St, Nashville, TN"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Describe the event..."
            />
          </div>

          <SignupOptionsField
            allowVolunteers={formData.allow_volunteers}
            allowAttendees={formData.allow_attendees}
            onChange={(next) => setFormData(prev => ({ ...prev, ...next }))}
          />

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? (isEditing ? 'Saving...' : 'Creating...')
                : (isEditing ? 'Save Changes' : 'Create Event')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAddEventModal;
